import React, { useState } from 'react';
import axios from 'axios';
import { Target, Pencil, Trophy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRESETS = [
  { label: "Universidad pública", amount: 250000 },
  { label: "Universidad privada", amount: 900000 },
  { label: "Estudios en el extranjero", amount: 1500000 },
  { label: "Maestría", amount: 400000 },
];

export const SavingsGoal = ({ account, projected, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(account.goal_amount ? String(account.goal_amount) : "");
  const [label, setLabel] = useState(account.goal_label || "");
  const [saving, setSaving] = useState(false);

  const goal = account.goal_amount;
  const pct = goal ? Math.min((account.balance / goal) * 100, 100) : 0;
  const reached = goal && account.balance >= goal;
  const projectedReaches = goal && projected >= goal;

  const openDialog = () => {
    setAmount(account.goal_amount ? String(account.goal_amount) : "");
    setLabel(account.goal_label || "");
    setOpen(true);
  };

  const save = async (removeGoal = false) => {
    const value = removeGoal ? null : parseFloat(amount);
    if (!removeGoal && (!value || value <= 0)) { toast.error("Ingresa una meta mayor a 0"); return; }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/accounts/${account.id}/goal`, { goal_amount: value, goal_label: removeGoal ? null : label.trim() || null });
      toast.success(removeGoal ? "Meta eliminada" : "Meta guardada");
      setOpen(false);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la meta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {goal ? (
        <div data-testid={`goal-block-${account.id}`} className="rounded-xl border border-gray-100 bg-white p-4 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-bold uppercase text-gray-500 flex items-center gap-1 truncate"><Target className="w-3.5 h-3.5 text-brand-blue" /> {account.goal_label || "Meta de ahorro"}</p>
            <button data-testid={`goal-edit-btn-${account.id}`} onClick={openDialog} className="text-gray-400 hover:text-brand-blue transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
          </div>
          <div className="h-3 w-full rounded-full bg-gray-100 overflow-hidden">
            <div data-testid={`goal-progress-bar-${account.id}`} className={`h-full rounded-full transition-[width] duration-700 ease-out ${reached ? "bg-brand-green" : "bg-brand-blue"}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center justify-between text-xs">
            <span data-testid={`goal-progress-pct-${account.id}`} className="font-bold text-brand-dark">{pct.toFixed(0)}%</span>
            <span className="text-gray-500">${account.balance.toLocaleString()} de ${goal.toLocaleString()}</span>
          </div>
          {reached ? (
            <p className="text-xs font-bold text-brand-green flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> ¡Meta alcanzada!</p>
          ) : (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              {projectedReaches ? <><Sparkles className="w-3.5 h-3.5 text-brand-green" /> La proyección a 18 años supera la meta</> : <>Faltan ${(goal - account.balance).toLocaleString()}</>}
            </p>
          )}
        </div>
      ) : (
        <button data-testid={`goal-set-btn-${account.id}`} onClick={openDialog} className="w-full rounded-xl border-2 border-dashed border-gray-200 p-3 text-sm font-bold text-gray-500 hover:border-brand-blue hover:text-brand-blue transition-colors flex items-center justify-center gap-2">
          <Target className="w-4 h-4" /> Fijar meta de ahorro
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle>Meta para {account.child_name}</DialogTitle>
            <DialogDescription>Elige un objetivo o escribe tu propia cifra. Verás el avance en la alcancía.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-2">
              {PRESETS.map((p) => (
                <button key={p.label} type="button" data-testid={`goal-preset-${p.amount}`} onClick={() => { setAmount(String(p.amount)); setLabel(p.label); }}
                  className={`rounded-xl border p-3 text-left transition-colors ${String(p.amount) === amount && label === p.label ? "border-brand-blue bg-brand-lightBlue" : "border-gray-200 hover:border-brand-blue"}`}>
                  <p className="text-xs font-bold text-brand-dark">{p.label}</p>
                  <p className="text-sm text-brand-blue font-extrabold">${p.amount.toLocaleString()}</p>
                </button>
              ))}
            </div>
            <div className="space-y-2">
              <Label>Nombre de la meta</Label>
              <Input data-testid="goal-label-input" maxLength={60} placeholder="Ej: Universidad" value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Monto objetivo ($)</Label>
              <Input data-testid="goal-amount-input" type="number" min="1" placeholder="Ej: 250000" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div className="flex gap-2">
              {goal && <Button variant="outline" data-testid="goal-remove-btn" onClick={() => save(true)} disabled={saving} className="flex-1 border-red-200 text-red-500 hover:bg-red-50">Quitar meta</Button>}
              <Button data-testid="goal-save-btn" onClick={() => save(false)} disabled={saving} className="flex-1 bg-brand-blue text-white font-bold">{saving ? "Guardando..." : "Guardar meta"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
